import type {
  ContentfulHeadlineBlock,
  ContentfulTextBlock,
} from "@scott/contentful/types";
import type { TextSectionProps } from "@/scott/ui/blocks/text-section/text-section";
import { getBlockId } from "@/services/contentful/assemblies/page/adapter/utils";
import type { SectionTextInput } from "@/services/contentful/components/SectionText/section-text.adapter";
import {
  UiConfigurationAdapter,
  type UiConfigurationInput,
} from "@/services/contentful/topics/UIConfiguration/ui-configuration.adapter";
import type {
  SectionIntroTextPageBlock,
  UiConfiguration,
} from "@/types/page-block";

export interface SectionIntroTextInput {
  sys?: { id: string };
  __typename: "SectionIntroText";
  internalName: string;
  containerWidth: "narrow" | "medium" | "wide";
  textVariant: "xlarge" | "large" | "normal" | "small";
  headline?: ContentfulHeadlineBlock;
  headlineIntro?: ContentfulHeadlineBlock;
  textIntro?: ContentfulTextBlock;
  headlineLevel?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  headlineVariant?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  anchorId?: string;
  uiConfiguration?: UiConfigurationInput;
}

const columnWidthMap = {
  narrow: 8,
  medium: 10,
  wide: 12,
} as const satisfies Record<
  SectionTextInput["containerWidth"],
  NonNullable<TextSectionProps["columnWidth"]>
>;

const textVariantMap = {
  normal: "p",
  large: "p-large",
  xlarge: "p-xlarge",
  small: "p-small",
} as const satisfies Record<
  NonNullable<SectionTextInput["textVariant"]>,
  NonNullable<TextSectionProps["textVariant"]>
>;

export function SectionIntroTextAdapter(
  item: SectionIntroTextInput,
  index: number
): SectionIntroTextPageBlock {
  const uiConfig: UiConfiguration | undefined = item.uiConfiguration
    ? UiConfigurationAdapter(item.uiConfiguration)
    : undefined;

  return {
    sys: item.sys,
    id: getBlockId(item, index),
    __typename: item.__typename,
    internalName: item.internalName,
    headline: item.headline,
    headlineIntro: item.headlineIntro,
    textIntro: item.textIntro,
    headlineLevel: item.headlineLevel,
    headlineVariant: item.headlineVariant,
    textVariant: textVariantMap[item.textVariant] ?? "p",
    columnWidth: columnWidthMap[item.containerWidth] ?? columnWidthMap.wide,
    outerContainerOnly: item.containerWidth === "wide",
    anchorId: item.anchorId,
    backgroundColor: uiConfig?.backgroundColor,
    headlineColor: uiConfig?.headlineColor,
    color: uiConfig?.textColor,
    paddingTop: uiConfig?.paddingTop,
    paddingBottom: uiConfig?.paddingBottom,
  };
}
